import { captureException } from "@sentry/react";
import type { CallStateCommandShapesWithoutMeta } from "@theirstory/common/src/call_state/commands";
import { calculateExponentialBackoff } from "@theirstory/common/utils";
import { select, takeEvery } from "redux-saga/effects";

import { getTheirStorySdkSaga } from "../../inheritFromWeeb-ui-v2/state/gqlClient";
import { sleep } from "../../../../components/utils/util";
import { LocalMediaStream } from "../../../../lib/localRecordingNew";
import type { RootState } from "../../rootReducer";
import { generateCallLocalRecordingCommands, generateCallStateCommands } from "../reportCallState";

export function* reportLocalRecordingCallState() {
  const commandsByStory: { [storyId: string]: CallStateCommandShapesWithoutMeta[] } = {};
  const reporterState: { [storyId: string]: boolean } = {};
  const client = yield getTheirStorySdkSaga();

  function* handleNewCommands(commands: CallStateCommandShapesWithoutMeta[], storyId: string, authToken?: string) {
    if (!commandsByStory[storyId]) {
      commandsByStory[storyId] = [];
    }

    commandsByStory[storyId].push(...commands);

    if (commands.length > 0) {
      console.log("Adding commands to the queue", commands, storyId);
    }

    yield reportCallState(storyId, authToken);
  }

  function* reportCallState(storyId: string, authToken?: string, retryCount = 0) {
    const clientToUse = authToken ? yield getTheirStorySdkSaga(authToken) : client;
    // We only want to bail if this storyId is already being reported,
    // AND we aren't currently retrying that report
    if (retryCount === 0 && !reporterState[storyId]) {
      try {
        const commands = [...commandsByStory[storyId]];
        if (commands && commands.length > 0) {
          reporterState[storyId] = true;
          console.log(`Reporting commands: [${commands.map((c) => c.type).join(", ")}]`);
          const response = yield clientToUse.updateStoryState({
            storyId,
            commands,
          });
          console.log(
            `Reported ${commands.length} commands, which turned into ${response.executeBatch.length} events, batch_id: ${response.executeBatch?.[0]?.batch_id}`,
            commands,
            response.executeBatch
          );
          commandsByStory[storyId].splice(0, commands.length);
        }
      } catch (e) {
        const backoffDelay = calculateExponentialBackoff(retryCount + 1);
        console.warn(
          `Failed to report call state commands, retrying for the ${retryCount + 1} time in ${backoffDelay}ms`,
          e
        );
        captureException(e);
        yield sleep(backoffDelay);
        yield reportCallState(storyId, authToken, retryCount + 1);
      } finally {
        reporterState[storyId] = false;
      }
    }
  }

  yield takeEvery("*", function* (action: any) {
    const state: RootState = yield select();

    // TODO: This check won't be neccesary once we get rid of the non-streaming uploader
    if (action?.data?.stream instanceof LocalMediaStream) {
      const stream: LocalMediaStream = action.data.stream;
      yield handleNewCommands(generateCallLocalRecordingCommands(action), stream.storyId, stream.uploadToken);
    }

    if (state.call.featureFlags?.localRecordingStreaming) {
      yield handleNewCommands(generateCallStateCommands(state, action), state.call.storyId);
    }
  });
}
